/**
 * soundEngine.js — Web Audio API synthesizer for Orboot celebrations
 *
 * All sounds are generated in-browser. No external files, no CORS, no loading.
 * The AudioContext is created lazily on first call (browsers require a user gesture).
 *
 * All public sounds respect the global sound preference (utils/soundPrefs).
 */
import { isSoundEnabled } from './soundPrefs';

let ctx = null;

function getCtx() {
  if (!ctx) {
    try {
      ctx = new (window.AudioContext || window.webkitAudioContext)();
    } catch (e) {
      return null;
    }
  }
  // Resume if suspended (autoplay policy)
  if (ctx.state === 'suspended') ctx.resume();
  return ctx;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

/** Play a single sine tone with attack + decay envelope */
function tone(freq, startTime, duration, gainPeak = 0.4, type = 'sine') {
  const c = getCtx();
  if (!c) return;

  const osc  = c.createOscillator();
  const gain = c.createGain();

  osc.connect(gain);
  gain.connect(c.destination);

  osc.type      = type;
  osc.frequency.setValueAtTime(freq, startTime);

  // Quick attack, longer decay
  const attack = 0.01;
  gain.gain.setValueAtTime(0, startTime);
  gain.gain.linearRampToValueAtTime(gainPeak, startTime + attack);
  gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration);

  osc.start(startTime);
  osc.stop(startTime + duration + 0.05);
}

/** Play a noise burst (for stamp / thud) */
function noiseBurst(startTime, duration = 0.08, gainPeak = 0.3) {
  const c = getCtx();
  if (!c) return;

  const bufferSize = c.sampleRate * duration;
  const buffer     = c.createBuffer(1, bufferSize, c.sampleRate);
  const data       = buffer.getChannelData(0);
  for (let i = 0; i < bufferSize; i++) data[i] = Math.random() * 2 - 1;

  const source = c.createBufferSource();
  source.buffer = buffer;

  // Low-pass filter to make it sound like a thud, not static
  const filter = c.createBiquadFilter();
  filter.type            = 'lowpass';
  filter.frequency.value = 200;

  const gain = c.createGain();
  gain.gain.setValueAtTime(gainPeak, startTime);
  gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration);

  source.connect(filter);
  filter.connect(gain);
  gain.connect(c.destination);

  source.start(startTime);
  source.stop(startTime + duration + 0.05);
}

// ─────────────────────────────────────────────────────────────────────────────
// Public sounds
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Discovery fanfare — cheerful ascending arpeggio (C5-E5-G5-C6).
 * Plays on first card discovery.
 */
export function playDiscovery() {
  if (!isSoundEnabled()) return;
  const c = getCtx();
  if (!c) return;

  const now   = c.currentTime;
  const notes = [523.25, 659.25, 783.99, 1046.50]; // C5-E5-G5-C6
  const step  = 0.10; // seconds between notes

  notes.forEach((freq, i) => {
    tone(freq, now + i * step, 0.35, 0.35, 'sine');
    // Add a soft triangle overtone for warmth
    tone(freq * 2, now + i * step, 0.20, 0.08, 'triangle');
  });

  // Final sparkle — rapid high notes
  [1318.5, 1567.98, 2093].forEach((freq, i) => {
    tone(freq, now + notes.length * step + i * 0.06, 0.18, 0.15, 'sine');
  });
}

/**
 * Soft pop — played on repeat card taps or minor interactions.
 */
export function playPop() {
  if (!isSoundEnabled()) return;
  const c = getCtx();
  if (!c) return;

  const now = c.currentTime;
  // Quick pitch-drop: sounds like a bubble pop
  const osc  = c.createOscillator();
  const gain = c.createGain();
  osc.connect(gain);
  gain.connect(c.destination);

  osc.type = 'sine';
  osc.frequency.setValueAtTime(600, now);
  osc.frequency.exponentialRampToValueAtTime(200, now + 0.08);

  gain.gain.setValueAtTime(0.3, now);
  gain.gain.exponentialRampToValueAtTime(0.001, now + 0.12);

  osc.start(now);
  osc.stop(now + 0.15);
}

/**
 * Stamp thud — played when the "New discovery!" badge lands.
 */
export function playStamp() {
  if (!isSoundEnabled()) return;
  const c = getCtx();
  if (!c) return;

  const now = c.currentTime;
  noiseBurst(now, 0.07, 0.4);
  // Add a low sine punch underneath
  tone(80, now, 0.12, 0.5, 'sine');
}

/**
 * Warm up the AudioContext on the very first user gesture.
 * Call this from any onClick handler before the first real sound.
 */
export function warmUp() {
  getCtx();
}
